/**
 * ocs — Object Coordinate System → World Coordinate System mapping for the
 * vector PDF exporter (PDF-5d).
 *
 * Entities authored in a tilted plane carry an **extrusion** vector (DXF group
 * codes 210/220/230) that is the OCS Z-axis (the plane normal). Their stored
 * 2D coordinates are in that plane's Object Coordinate System and must be
 * lifted to WCS before the planar plot projects them onto the page.
 *
 * Used by the **Text / Block** converters, whose insertion points ARE stored in
 * OCS (DXF group 10 in the extrusion plane). NOTE the **ELLIPSE** is the
 * exception: although it carries an `extrusion`, its `center` (10/20/30) and
 * `majorAxisEndpoint` (11/21/31) are stored in WCS, so `curveConverter`
 * PROJECTS the 3D ellipse (SL-15 round-9 F3) instead of OCS-lifting it here —
 * the extrusion only fixes the in-plane minor direction. Circle / Arc / Line /
 * LWPolyline / Spline / Hatch have no extrusion in the schema — they are always
 * authored in the WCS XY plane (Z up), so they never need OCS conversion here.
 *
 * # DXF Arbitrary Axis Algorithm (the reference impl)
 * Given a unit normal N = (nx, ny, nz), DXF derives the OCS X / Y axes so the
 * basis is reproducible (otherwise an arbitrary in-plane rotation would be
 * ambiguous). The algorithm (Autodesk DXF reference "Arbitrary Axis Algorithm";
 * `ezdxf` `ezdxf.math.OCS`):
 *
 *   - if |nx| < 1/64 AND |ny| < 1/64:  Ax = WY × N   (WY = world Y = (0,1,0))
 *   - else                             Ax = WZ × N   (WZ = world Z = (0,0,1))
 *   then normalise Ax, and Ay = N × Ax (already unit since N ⟂ Ax).
 *
 * The OCS→WCS lift of an OCS point (x, y) at elevation `z` along N is:
 *   P_wcs = x·Ax + y·Ay + z·N
 *
 * For the PLANAR plot we project P_wcs onto the world XY plane (drop the world
 * Z), which is what the rest of the pipeline (page transform / VP transform)
 * consumes — every other converter already emits world XY mm.
 *
 * NOTE: a fully general 3D plot would honour the view direction; the current
 * exporter is an orthographic top-down (world XY) projection like the editor's
 * 2D plot, so dropping world Z after the OCS lift is correct for the parity
 * scope. A non-(0,0,1) extrusion most commonly differs only by an in-plane
 * rotation / mirror once projected (e.g. (0,0,-1) ⇒ X mirror), which the lift
 * reproduces exactly.
 */

import type { DrawingPoint3D } from "@dxf-ai-editor/drawing-model";

import { computeOcsBasis, isDefaultOcsNormal, ocsToWcs3d } from "../../../geometry/ocsBasis";
import type { Point2 } from "../cadPrimitive";

/**
 * Whether the extrusion normal is the default (0,0,1) WCS Z (within a small
 * tolerance). The overwhelmingly common case — the OCS→WCS lift is the identity
 * for the planar plot, so callers skip the transform entirely (and the existing
 * tests stay byte-identical). Re-exported from the shared `ocsBasis` one-truth
 * (see its doc for the exact tolerance rule) so existing importers of this
 * module keep resolving.
 */
export const isDefaultOcs = isDefaultOcsNormal;

/**
 * Build the OCS→WCS-XY map for the given extrusion normal via the DXF Arbitrary
 * Axis Algorithm (`../../../geometry/ocsBasis` — the shared basis computation;
 * this module supplies only the PLANAR-plot-specific "drop world Z"
 * projection). Returns a function mapping an OCS point (x, y) [+ optional
 * elevation `elev` along N] to its WCS XY projection `{x, y}`.
 *
 * For the default (≈(0,0,1)) normal this is the identity (fast-path), so the
 * common case adds no math. For a tilted / mirrored normal the returned closure
 * lifts (x,y,elev) into WCS via x·Ax + y·Ay + elev·N and drops world Z.
 */
export function ocsToWcs2d(
  normal: DrawingPoint3D | undefined,
): (x: number, y: number, elev?: number) => Point2 {
  if (isDefaultOcsNormal(normal)) {
    return (x, y) => ({ x, y });
  }
  const basis = computeOcsBasis(normal);
  return (x, y, elev = 0) => {
    // P_wcs = x·Ax + y·Ay + elev·N — project onto world XY (drop world Z).
    const p = ocsToWcs3d(basis, x, y, elev);
    return { x: p.x, y: p.y };
  };
}
